import { HeartBank } from "../../../managers/HeartBank.ts";
import template from "./sw-movie.mjs";
import { render } from "lit-html";
import Strings from "../../../models/Strings.json";
import Collection from "../../../models/Movies.json";
import { Movies, ProgressHandler } from "../../../managers/Movies.ts";

import { Modal } from "flowbite";
import type { ModalOptions, ModalInterface } from "flowbite";
import type { InstanceOptions } from "flowbite";
import { LANGUAGE, isProduction } from "../../global.ts";

const strings: any = Strings;
const collection: any = Collection;

export class SwMovie extends HTMLElement {
    private heartbank = new HeartBank();
    private movies = new Movies();

    private youtube: YT.Player | null = null;
    private youTubeTimer: number | null = null;
    private videoTimer: number | null = null;
    private duration = 1000 * (isProduction ? 1 : 1); // 1 second // faster causes lagging
    private hasSeeked = false;

    private modal: ModalInterface;
    private movie: HTMLElement;
    private heading: HTMLHeadingElement;
    private iframe: HTMLElement;
    private video: HTMLVideoElement;

    private download: HTMLButtonElement;
    private pause: HTMLButtonElement;
    private trash: HTMLButtonElement;

    private status: HTMLElement;
    private bar: HTMLProgressElement;
    private progress: HTMLSpanElement;

    private prev: HTMLButtonElement;
    private next: HTMLButtonElement;

    // defaults
    private readonly movieTitle = "buddha";
    private readonly collection: any = {
        buddha: {
            episode: 1,
            times: [0, 0, 0, 0, 0]
        }
    };

    constructor() {
        super();
        render(template(null), this);

        this.movie = this.querySelector("#sw-movie")!;
        this.heading = this.querySelector("#sw-movie-title")!;
        this.iframe = this.querySelector("#sw-movie-container")!;
        this.video = this.querySelector("#sw-movie-video")!;

        this.download = this.querySelector("#sw-movie-download")!;
        this.pause = this.querySelector("#sw-movie-pause")!;
        this.trash = this.querySelector("#sw-movie-trash")!;

        this.status = this.querySelector("#sw-movie-status")!;
        this.bar = this.querySelector("#sw-movie-bar")!;
        this.progress = this.querySelector("#sw-movie-progress")!;

        this.prev = this.querySelector("#sw-movie-prev")!;
        this.next = this.querySelector("#sw-movie-next")!;

        const modalOptions: ModalOptions = {
            placement: "center",
            backdrop: "dynamic",
            backdropClasses:
                "bg-gray-900/95 dark:bg-gray-900/95 fixed inset-0 z-40",
            closable: false,
            onHide: () => {
                this.youtube?.pauseVideo();
                this.video.pause();
                //window.location.hash = "#";
            },
            onShow: () => {
                this.movie.style.zIndex = "70";
                //console.log("modal is shown");
            },
            onToggle: () => {
                //console.log("modal has been toggled");
            }
        };

        // instance options object
        const instanceOptions: InstanceOptions = {
            id: "sw-movie",
            override: true
        };

        this.modal = new Modal(this.movie, modalOptions, instanceOptions);
    }

    private getCollection() {
        const storedCollection = localStorage.getItem("collection");
        return storedCollection
            ? JSON.parse(storedCollection)
            : this.collection;
    }

    private getEpisode(id: string | null = null) {
        if (id) return parseInt(id.split("-")[1]);
        else {
            const storedCollection = this.getCollection();
            return storedCollection[this.movieTitle].episode;
        }
    }

    private setEpisode(episode: number) {
        const storedCollection = this.getCollection();
        storedCollection[this.movieTitle].episode = episode;
        localStorage.setItem("collection", JSON.stringify(storedCollection));
    }

    private getTime(episode: number) {
        const storedCollection = this.getCollection();
        return storedCollection[this.movieTitle].times[episode - 1];
    }

    private setTime(episode: number, time: number) {
        const storedCollection = this.getCollection();
        storedCollection[this.movieTitle].times[episode - 1] = time;
        localStorage.setItem("collection", JSON.stringify(storedCollection));
    }

    private getMovieId(episode: number = 0) {
        return this.movieTitle + "-" + (episode || this.getEpisode());
    }

    private isOnPage(id: string): boolean {
        return (
            this.modal.isVisible() && this.getEpisode() === this.getEpisode(id)
        );
    }

    private videoStart() {
        this.youtube?.pauseVideo(); // need to be here
        this.youtubeStop();
        if (!this.videoTimer) {
            this.videoTimer = window.setInterval(
                () => this.setTime(this.getEpisode(), this.video.currentTime),
                this.duration
            );
            // this.videoTimer = window.setInterval(
            //     () =>
            //         this.movies.setTime(
            //             this.getMovieId(),
            //             this.video.currentTime
            //         ),
            //     this.duration
            // );
        }
    }

    private youtubeStart() {
        this.video.pause(); // need to be here
        this.videoStop();
        if (!this.youTubeTimer) {
            this.youTubeTimer = window.setInterval(
                () =>
                    this.setTime(
                        this.getEpisode(),
                        this.youtube?.getCurrentTime() || 0
                    ),
                this.duration
            );
            // this.youTubeTimer = window.setInterval(
            //     () =>
            //         this.movies.setTime(
            //             this.getMovieId(),
            //             this.youtube?.getCurrentTime() || 0
            //         ),
            //     this.duration
            // );
        }
    }

    private videoStop() {
        if (this.videoTimer) {
            clearInterval(this.videoTimer);
            this.videoTimer = null;
        }
    }

    private youtubeStop() {
        if (this.youTubeTimer) {
            clearInterval(this.youTubeTimer);
            this.youTubeTimer = null;
        }
    }

    connectedCallback() {
        this.loadYouTubeAPI();
        this.resume();

        this.prev.addEventListener("click", this.goTo.bind(this, false));
        this.next.addEventListener("click", this.goTo.bind(this, true));

        //this.video.addEventListener("play", () => console.log("play() called"));
        this.video.addEventListener("playing", this.videoStart.bind(this));
        this.video.addEventListener("pause", () => {
            this.videoStop();
            this.heartbank.alertAdmin(
                `video/pause/episode/${this.getEpisode()}/${
                    this.video.currentTime
                }`
            );
        });
        this.video.addEventListener("ended", () => {
            this.videoStop();
            this.goTo(true);
            this.heartbank.alertAdmin(
                `video/end/episode/${this.getEpisode()}/${
                    this.video.currentTime
                }`
            );
        });
        // 🔹 Listen for time updates (fires ~4 times per second) // causes lagging
        // this.video.addEventListener(
        //     "timeupdate",
        //     () => this.setTime(this.getEpisode(), this.video.currentTime)
        //     //this.movies.setTime(this.getMovieId(), this.video.currentTime)
        // );

        // handle safari error
        this.video.addEventListener("error", (event) => {
            const target = event.currentTarget as HTMLVideoElement;
            const error = target.error;
            if (error) {
                switch (error.code) {
                    case MediaError.MEDIA_ERR_ABORTED:
                        console.error("Video loading aborted.");
                        break;
                    case MediaError.MEDIA_ERR_NETWORK:
                        console.error("Network error while loading video.");
                        break;
                    case MediaError.MEDIA_ERR_DECODE:
                        console.error("Video decoding error.");
                        break;
                    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                        //console.error("Video source not supported or missing.");
                        if (
                            target.dataset.id &&
                            this.isOnPage(target.dataset.id)
                        )
                            alert(strings.movie17[LANGUAGE]); // this will loop until successful: another way is to ask user to confrim reload page
                        break;
                    default:
                        console.error("An unknown video error occurred.");
                        break;
                }
            } else {
                console.error("Unknown video error (no MediaError).");
            }
        });
        this.video.addEventListener("click", async () => {
            if (this.video.dataset.id && this.video.error) {
                const movie = await this.movies.get(this.video.dataset.id);
                if (movie.status === "completed" && movie.blob) {
                    this.video.src = URL.createObjectURL(movie.blob);
                    this.video.currentTime = this.getTime(
                        this.getEpisode(movie.id)
                    ); // movie.time;
                    this.video.play(); //await
                }
            }
        });

        // start or resume
        this.download.addEventListener("click", () => {
            if (!navigator.onLine) {
                alert(strings.offline[LANGUAGE]);
                return;
            }

            const id = this.getMovieId();
            this.renderStatus(id, "started");
            this.movies
                .download(id, this.handleProgress)
                .then(() => this.handleFinish(id))
                .catch(console.error);
        });

        this.pause.addEventListener("click", () => {
            const id = this.getMovieId();
            this.renderStatus(id, "paused");
            this.movies.pause(id);
        });

        this.trash.addEventListener("click", async () => {
            if (confirm(strings.movie13[LANGUAGE])) {
                const id = this.getMovieId();
                await this.movies.remove(id);
                this.renderMovie(this.getEpisode());
            }
        });

        window.addEventListener("online", () => {
            console.log("✅ You are back online");
            this.resume();
        });

        window.addEventListener("offline", async () => {
            console.log("⚠️ You are offline");
            const movies = await this.movies.getAllStarted();
            for (const movie of movies) {
                this.renderStatus(movie.id, "paused");
                //await this.movies.pause(movie.id); // keep status as started to be resumed
            }
        });

        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") {
                console.log("App is going to background");
                this.pauseAll();
            } else {
                console.log("App is foregrounded again");
            }
        });

        window.addEventListener("pagehide", () => {
            console.log("Page hidden (might be closing)");
            this.pauseAll();
        });

        window.addEventListener("beforeunload", () => {
            this.pauseAll();
        });
    }

    private pauseAll() {
        this.video.pause();
        this.youtube?.pauseVideo();

        // Use navigator.sendBeacon() or fetch(..., { keepalive: true })
    }

    // resume any unfinished downloads
    private async resume() {
        if (!navigator.onLine) {
            //alert(strings.offline[LANGUAGE]);
            return;
        }

        const movies = await this.movies.getAllStarted();
        for (const movie of movies) {
            this.renderStatus(movie.id, "started");
            this.movies
                .download(movie.id, this.handleProgress)
                .then(() => this.handleFinish(movie.id))
                .catch(console.error);
        }
    }

    private handleProgress: ProgressHandler = (id, p, bytes, total) => {
        if (this.isOnPage(id)) {
            this.bar.value = p;
            this.progress.textContent = `${
                strings["movie" + this.getEpisode()][LANGUAGE]
            }: ${p}% - ${(bytes / 1_000_000).toFixed(0)} / ${(
                total / 1_000_000
            ).toFixed(0)} MB`;
        }
    };

    private async handleFinish(id: string) {
        const movie = await this.movies.get(id);
        switch (movie.status) {
            case "completed":
                this.heartbank.alertAdmin("video/download/completed: ", id);
                if (this.isOnPage(id))
                    await this.renderMovie(this.getEpisode(id));
                alert(strings.movie15[LANGUAGE]);
                break;
            // case "paused":
            //     alert(strings.movie16[LANGUAGE]);
            //     this.renderStatus(id, "paused");
            //     break;
            case "failed":
                this.heartbank.alertAdmin("video/download/failed: ", id);
                alert(strings.movie16[LANGUAGE]); //movie10
                this.renderStatus(id, "failed");
                break;
        }

        // const status = await this.movies.verify(id);
        // switch (status) {
        //     case "incomplete":
        //         // user pauses download
        //         break;
        //     case "failed":
        //         alert(strings.movie10[LANGUAGE]);
        //         this.renderStatus(id, "failed");
        //         break;
        //     case "verified":
        //         if (this.isOnPage(id))
        //             await this.renderMovie(this.getEpisode(id));
        //         alert(strings.movie15[LANGUAGE]);
        //         break;
        // }
    }

    // private handleError(id: string, error: Error) {
    //     console.error(error);
    //     alert(strings.movie10[LANGUAGE]);
    //     this.renderStatus(id, "failed");
    // }

    private async renderStatus(
        id: string,
        status?: "started" | "paused" | "failed" | "completed"
    ) {
        if (this.isOnPage(id))
            switch (status) {
                case "started":
                    this.status.classList.remove("hidden");
                    this.download.classList.add("hidden");
                    this.pause.classList.remove("hidden");
                    this.trash.classList.add("hidden");
                    break;
                case "paused":
                    this.status.classList.add("hidden");
                    this.download.classList.remove("hidden");
                    this.pause.classList.add("hidden");
                    this.trash.classList.remove("hidden");
                    break;
                case "failed":
                case "completed":
                    this.status.classList.add("hidden");
                    this.download.classList.add("hidden");
                    this.pause.classList.add("hidden");
                    this.trash.classList.remove("hidden");
                    break;
                default: // undefined: newly created
                    this.status.classList.add("hidden");
                    this.download.classList.remove("hidden");
                    this.pause.classList.add("hidden");
                    this.trash.classList.add("hidden");
            }
    }

    public async show() {
        this.modal.show(); // order first bc loading below can take awhile on slow phone
        await this.loadYouTubeAPI();
        if (navigator.onLine) this.youtube = await this.initializeYouTube();
        this.renderMovie(); //await
    }

    private hide() {
        this.modal.hide();
    }

    private goTo(action: boolean) {
        const episode = this.getEpisode();

        let part: number;
        if (episode === 1) {
            part = action ? 2 : 5;
        } else if (episode === 5) {
            part = action ? 1 : 4;
        } else {
            part = episode + (action ? 1 : -1);
        }

        this.renderMovie(part);
    }

    // isPlay = false => for youtube paging
    private async renderMovie(episode: number = 0, isPlay = true) {
        episode = episode || this.getEpisode();
        const movie = await this.movies.get(this.getMovieId(episode));
        this.heading.textContent = `${strings.movie6[LANGUAGE]} - ${
            strings["movie" + episode][LANGUAGE]
        }`;
        this.setEpisode(episode);

        if (isPlay) {
            if (movie.status === "completed" && movie.blob) {
                this.iframe.classList.add("hidden");
                this.youtube?.pauseVideo();

                this.video.classList.remove("hidden");
                this.video.dataset.id = movie.id;
                this.video.src = URL.createObjectURL(movie.blob);
                this.video.currentTime = this.getTime(episode); // movie.time;
                this.video.play(); //await
            } else {
                this.iframe.classList.toggle("hidden", !this.youtube);
                this.youtube?.playVideoAt(episode - 1); //await
                //this.iframe.src = collection[this.movieTitle].youtube[episode - 1];

                this.video.classList.toggle("hidden", !!this.youtube);
                this.video.removeAttribute("src");
                this.video.load(); // needed to stop previous playing and show poster // // doesn't work on safari
                //this.video.pause();
            }
        }

        this.renderStatus(movie.id, movie.status);
        if (episode === 1) {
            this.prev.textContent = "← " + strings["movie5"][LANGUAGE];
            this.next.textContent = strings["movie2"][LANGUAGE] + " →";

            this.prev.classList.add("hidden");
            this.next.classList.remove("hidden");
        } else if (episode === 5) {
            this.prev.textContent = "← " + strings["movie4"][LANGUAGE];
            this.next.textContent = strings["movie1"][LANGUAGE] + " →";

            this.prev.classList.remove("hidden");
            this.next.classList.add("hidden");
        } else {
            this.prev.textContent =
                "← " + strings["movie" + (episode - 1)][LANGUAGE];
            this.next.textContent =
                strings["movie" + (episode + 1)][LANGUAGE] + " →";

            this.prev.classList.remove("hidden");
            this.next.classList.remove("hidden");
        }
    }

    // will only load one time
    private loadYouTubeAPI(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!navigator.onLine || window.YT?.Player) {
                resolve();
            }

            const script = document.createElement("script");
            script.src = "https://www.youtube.com/iframe_api";
            script.async = true;

            script.onload = () => {
                (window as any).onYouTubeIframeAPIReady = () => {
                    resolve();
                };
            };

            script.onerror = () => {
                reject(new Error("Failed to load YouTube IFrame API"));
            };

            document.head.appendChild(script);
        });
    }

    // will only load one time
    private initializeYouTube(): Promise<YT.Player> {
        return new Promise((resolve, reject) => {
            if (this.youtube) resolve(this.youtube);
            else
                new YT.Player("sw-movie-iframe", {
                    host: "https://www.youtube.com",
                    //videoId,
                    //height: "315",
                    //width: "560",
                    playerVars: {
                        listType: "playlist",
                        list: collection[this.movieTitle].playlist,
                        controls: 1,
                        autoplay: 0,
                        mute: 0,
                        modestbranding: 1,
                        rel: 0,
                        showinfo: 0,
                        playsinline: 1,
                        origin: window.location.origin
                    },
                    events: {
                        onReady: (event: YT.PlayerEvent) => {
                            //console.log("YouTube Player is ready");
                            resolve(event.target);
                        },
                        onStateChange: async (event: YT.OnStateChangeEvent) => {
                            const episode = this.getEpisode();
                            //const id = this.getMovieId();
                            const time = this.getTime(episode); //await this.movies.getTime(id);

                            switch (event.data) {
                                case YT.PlayerState.UNSTARTED: // -1 player initialized
                                    this.hasSeeked = false;
                                    break;
                                case YT.PlayerState.CUED: // metadata loaded; ready to play
                                    this.hasSeeked = false;
                                    break;
                                case YT.PlayerState.BUFFERING: // 3 video data loading
                                    this.youtubeStop();
                                    break;
                                case YT.PlayerState.PLAYING:
                                    this.youtubeStart();
                                    if (!this.hasSeeked) {
                                        this.youtube?.seekTo(time, true);
                                        this.hasSeeked = true;
                                        this.heartbank.alertAdmin(
                                            `youtube/watch/episode/${episode}/${time}`
                                        );
                                    } else
                                        this.renderMovie(
                                            event.target.getPlaylistIndex() + 1,
                                            false
                                        );
                                    break;
                                case YT.PlayerState.PAUSED:
                                    this.youtubeStop();
                                    this.heartbank.alertAdmin(
                                        `youtube/pause/episode/${episode}/${time}`
                                    );
                                    break;
                                case YT.PlayerState.ENDED:
                                    this.youtubeStop();
                                    this.goTo(true);
                                    this.heartbank.alertAdmin(
                                        `youtube/end/episode/${episode}/${time}`
                                    );
                                    break;
                            }
                        },
                        onError: (event: YT.OnErrorEvent) => {
                            //console.error("YouTube Player Error: ", event.data);
                            reject(
                                new Error(`YouTube Player Error: ${event.data}`)
                            );
                        }
                    }
                });
        });
    }
}
